import React from "react";
import { Button, Menu, MenuItem } from "@mui/material";
import download from "downloadjs";
import Spinner from "../../components/Spinner";
import { Favourites } from "./Favourites";
import { Box } from "@mui/system";

type ContextMenuState = {
  mouseX: number;
  mouseY: number;
  soundName: string;
};

const ContextMenu = ({
  contextMenu,
  setContextMenu,
}: {
  contextMenu: ContextMenuState | null;
  setContextMenu: React.Dispatch<React.SetStateAction<ContextMenuState | null>>;
}) => {
  const getFavourites = () => {
    return JSON.parse(
      localStorage.getItem("HegBot-SoundBoard-Favourites") ?? "[]"
    ) as string[];
  };

  const addFavourite = (soundName: string) => {
    if (!getFavourites().includes(soundName)) {
      localStorage.setItem(
        "HegBot-SoundBoard-Favourites",
        JSON.stringify([...getFavourites(), soundName])
      );
    }
  };

  const removeFavourite = (soundName: string) => {
    localStorage.setItem(
      "HegBot-SoundBoard-Favourites",
      JSON.stringify(getFavourites().filter((x) => x !== soundName))
    );
  };

  const handleClose = () => {
    setContextMenu(null);
  };

  const soundName = contextMenu?.soundName ?? "NO-CM";
  const isFavourite = getFavourites().includes(soundName);

  return (
    <Menu
      open={contextMenu !== null}
      onClose={handleClose}
      anchorReference="anchorPosition"
      anchorPosition={
        contextMenu !== null
          ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
          : undefined
      }
      id={`${soundName}-context-${isFavourite}`}
    >
      <MenuItem
        onClick={() => {
          handleClose();
          download(`https://api.heg.cool/hegbot/soundboard/${soundName}`);
        }}
      >
        Save
      </MenuItem>
      {isFavourite ? (
        <MenuItem
          onClick={() => {
            handleClose();
            removeFavourite(soundName);
          }}
        >
          Unfavourite
        </MenuItem>
      ) : (
        <MenuItem
          onClick={() => {
            handleClose();
            addFavourite(soundName);
          }}
        >
          Favourite
        </MenuItem>
      )}
    </Menu>
  );
};

type ButtonProps = {
  searchTerm?: string;
  clips?: string[];
  clickFunction?: (s: string) => void;
  date?: Date;
};

export default function SoundButtons({
  searchTerm = "",
  clips = [],
  clickFunction = () => {},
  date = new Date(),
}: ButtonProps) {
  const [contextMenu, setContextMenu] = React.useState<ContextMenuState | null>(
    null
  );

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
            soundName: (event.target as HTMLButtonElement)
              .textContent as string,
          }
        : null
    );
  };

  const isAprilFools: boolean = date.getMonth() === 3 && date.getDate() === 1;

  const filteredItems =
    clips?.filter((it) => it.includes(searchTerm ?? "")) ?? clips;
  const alphaGroups = filteredItems?.reduce(
    (acc: Map<string, string[]>, item: string) => {
      const firstLetter: string = item.trim()[0];

      if (!acc.get(firstLetter)) {
        acc.set(firstLetter, []);
      }
      acc.get(firstLetter)?.push(item);

      return acc;
    },
    new Map<string, string[]>()
  );
  const buttonWidth = Math.max(...filteredItems.map((i) => i.length), 17) + 1;

  const SoundButton = ({ soundName }: { soundName: string }) => (
    <Button
      type="button"
      sx={{
        width: `${buttonWidth}ch`,
        color: "white",
        border: "1px solid black",
        borderRadius: "12px",
        backgroundColor: "rgb(74, 74, 74)",
        height: "4em",
        textTransform: "none",
        margin: "2px",
        zIndex: 0,
        "&:hover": {
          border: `2px solid white`,
          backgroundColor: "rgb(74, 74, 74)",
        },
      }}
      fullWidth
      id={soundName}
      key={soundName}
      variant="outlined"
      size="medium"
      onClick={() => clickFunction(soundName)}
      onContextMenu={handleContextMenu}
    >
      {isAprilFools ? "stef-peaks-mid" : soundName}
    </Button>
  );

  if (!filteredItems) {
    return (
      <div id="buttons-div">
        <Spinner />
      </div>
    );
  }

  return (
    <div id="buttons-div">
      <p>{filteredItems?.length} items</p>
      <Box sx={{ display: "flex", paddingX: "1%" }}>
        <Button
          type="button"
          id="random"
          key="random"
          className="button"
          variant="outlined"
          sx={{
            color: "white",
            border: "1px solid black",
            backgroundColor: "rgb(74, 74, 74)",
            height: "4em",
            textTransform: "none",
            borderRadius: "12px",
            paddingX: "2em",
            "&:hover": {
              border: "2px solid white",
              backgroundColor: "rgb(74, 74, 74)",
            },
          }}
          onClick={() =>
            clickFunction(
              filteredItems[Math.floor(Math.random() * filteredItems.length)]
            )
          }
        >
          Play Random
        </Button>
      </Box>
      <Favourites
        clickFunction={clickFunction}
        rightClickFunction={handleContextMenu}
      />
      {Array.from(alphaGroups.keys()).map((key) => {
        const list: string[] = alphaGroups.get(key) ?? [];
        if (!list || list.length < 0) {
          return null;
        }
        return (
          <>
            <Box
              sx={{ display: "flex", flexDirection: "row", marginX: "0.5em" }}
            >
              {key.toUpperCase()}
            </Box>
            <hr />
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                marginX: "0.5em",
              }}
            >
              {list.map((soundName) =>
                SoundButton({
                  soundName,
                })
              )}
            </Box>
          </>
        );
      })}
      <ContextMenu contextMenu={contextMenu} setContextMenu={setContextMenu} />
    </div>
  );
}
